import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

/**
 * System update function signature. Each game system implements this.
 * Returns an array of events that occurred during this tick.
 */
export type SystemUpdateFn = (
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
) => GameEvent[];

/**
 * The GameLoop advances the simulation by one tick.
 * It calls each registered system in order and collects all emitted events.
 *
 * Systems are executed in this order:
 * 1. CompetitorSystem — rival releases happen independently
 * 2. ContractSystem — contract progress
 * 3. ProductionSystem — staff contribute to projects
 * 4. TrainingSystem — training countdown and completion
 * 5. LevelingSystem — XP gain, level ups
 * 6. MoraleSystem — satisfaction, demands, departures
 * 7. MarketSystem — sales decay, revenue
 * 8. FinanceSystem — weekly salary, rent, debt
 * 9. EventSystem — random and scripted events
 * 10. UnlockSystem — check unlock conditions
 * 11. HiringSystem — refresh candidate pool
 * 12. PrestigeSystem — campaign end detection
 */
export class GameLoop {
  private systems: { name: string; update: SystemUpdateFn }[] = [];

  registerSystem(name: string, update: SystemUpdateFn): void {
    this.systems.push({ name, update });
  }

  /**
   * Advance the simulation by one tick.
   * Returns all events emitted during this tick.
   */
  tick(state: SimulationState, themeData: ThemeData, rng: SeededRandom): GameEvent[] {
    if (state.isPaused || state.speed === 0) {
      return [];
    }

    const allEvents: GameEvent[] = [];

    // Housekeeping: clean up stale team member IDs (staff who left/were fired)
    const staffIds = new Set(state.staff.map((s) => s.id));
    for (const team of state.teams) {
      const before = team.memberIds.length;
      team.memberIds = team.memberIds.filter((id) => staffIds.has(id));
      if (team.memberIds.length < before && team.leaderId && !staffIds.has(team.leaderId)) {
        team.leaderId = null;
      }
    }

    // Advance tick counter
    state.company.currentTick++;

    // Calculate date from tick
    const ticksPerWeek = themeData.balance.ticksPerDay * themeData.balance.daysPerWeek;
    const totalTicks = state.company.currentTick;
    const totalWeeks = Math.floor(totalTicks / ticksPerWeek);
    state.company.currentWeek = (totalWeeks % themeData.balance.weeksPerYear) + 1;
    state.company.currentYear = Math.floor(totalWeeks / themeData.balance.weeksPerYear) + 1;

    // Run each system in order
    for (const system of this.systems) {
      try {
        const events = system.update(state, themeData, rng);
        allEvents.push(...events);
      } catch (error) {
        // In production: skip the broken system, log the error
        // In dev: rethrow for debugging
        if (import.meta.env?.DEV) {
          throw error;
        }
        console.error(`System "${system.name}" threw on tick ${state.company.currentTick}:`, error);
      }
    }

    return allEvents;
  }

  /** Returns the number of registered systems (for testing). */
  get systemCount(): number {
    return this.systems.length;
  }
}
